import { IconProps } from "lib/icons/untitled_ui/UIcon";

/**
 * The revealed contact channels — phone, email and LinkedIn — as data, shared by every surface
 * that shows them. The contacts table is the source of truth for what a contact's details look
 * like and what you can do with them; this module is that definition, lifted out so the lead's
 * contact cards render the identical set rather than a reduced copy of it.
 *
 * Data only: rendering lives in {@link ContactChannelList}, so a builder can be unit-tested and
 * so a caller can render the same rows in a different layout (a table cell, a card column).
 */

/**
 * The channel-bearing fields a contact supplies. Structural rather than tied to one GraphQL
 * type: a search row, a revealed suggestion and a saved contact all satisfy it, and none of
 * them share a generated type.
 *
 * The `has*` flags are the resting (unrevealed) signal — what is on file before anything is
 * fetched. A surface with nothing to reveal (a saved contact) simply omits them.
 */
export type ContactChannelData = {
  email?: string | null;
  mobilePhoneNumbers?: readonly (string | null)[] | null;
  directPhoneNumbers?: readonly (string | null)[] | null;
  linkedinUrl?: string | null;
  hasEmail?: boolean | null;
  hasMobilePhoneNumbers?: boolean | null;
  hasDirectPhoneNumbers?: boolean | null;
  hasLinkedinUrl?: boolean | null;
};

/** What a channel's line renders. Discriminated so the list component owns every value's markup. */
export type ChannelValueSpec =
  | { kind: "phones"; numbers: string[] }
  | { kind: "email"; email: string }
  | { kind: "linkedin"; url: string };

/**
 * One channel line. `ariaLabel` names the icon-only phone rows, so mobile and direct are
 * distinguishable to a screen reader as well as by glyph.
 */
export type ChannelRow = {
  key: string;
  icon: IconProps["icon"];
  ariaLabel?: string;
  /** On file per the contact's `has*` flag — drives the resting glyph before a reveal. */
  available: boolean;
  /** Renders as a link out, so the whole line shares one hover treatment. */
  isLink: boolean;
  skeletonWidth: string;
  /** The revealed value, or null when this channel came back empty. */
  value: ChannelValueSpec | null;
  copy?: { value: string; label: string; kind: "phone" | "email" };
};

function nonNullStrings(values: readonly (string | null)[] | null | undefined): string[] {
  return (values ?? []).filter((n): n is string => n !== null);
}

// Phone channels: both sources can carry either type. Direct leads, then mobile — the
// order the design lists them in (handset glyph above smartphone).
function phoneRows(contact: ContactChannelData): ChannelRow[] {
  const directNumbers = nonNullStrings(contact.directPhoneNumbers);
  const mobileNumbers = nonNullStrings(contact.mobilePhoneNumbers);
  return [
    phoneRow({
      key: "direct",
      // Handset glyph — a desk line, as opposed to the mobile's smartphone.
      icon: "phone",
      ariaLabel: "Direct",
      available: !!contact.hasDirectPhoneNumbers,
      numbers: directNumbers,
      copyLabel: "Copy direct number to clipboard",
    }),
    phoneRow({
      key: "mobile",
      // Smartphone glyph, distinct from the handset used for a direct line.
      icon: "phone01",
      ariaLabel: "Mobile",
      available: !!contact.hasMobilePhoneNumbers,
      numbers: mobileNumbers,
      copyLabel: "Copy mobile number to clipboard",
    }),
  ];
}

function phoneRow({
  key,
  icon,
  ariaLabel,
  available,
  numbers,
  copyLabel,
}: {
  key: string;
  icon: IconProps["icon"];
  ariaLabel: string;
  available: boolean;
  numbers: string[];
  copyLabel: string;
}): ChannelRow {
  return {
    key,
    icon,
    ariaLabel,
    available,
    isLink: false,
    skeletonWidth: "96px",
    value: numbers.length > 0 ? { kind: "phones", numbers } : null,
    // Only the first number is copyable; the rest live behind the "+N" tooltip.
    ...(numbers.length > 0
      ? { copy: { value: numbers[0], label: copyLabel, kind: "phone" as const } }
      : {}),
  };
}

function emailRows(contact: ContactChannelData): ChannelRow[] {
  return [
    {
      key: "email",
      icon: "mail01",
      available: !!contact.hasEmail,
      // A mailto link, styled and hovered like the LinkedIn line.
      isLink: true,
      skeletonWidth: "120px",
      value: contact.email ? { kind: "email", email: contact.email } : null,
      ...(contact.email
        ? {
            copy: {
              value: contact.email,
              label: "Copy email to clipboard",
              kind: "email" as const,
            },
          }
        : {}),
    },
  ];
}

// LinkedIn joins phone and email in the channel list. No copy affordance — once
// revealed it is a link out, not a value you'd paste.
function linkedinRows(contact: ContactChannelData): ChannelRow[] {
  return [
    {
      key: "linkedin",
      icon: "linkedin",
      // Resting, the glyph is all a screen reader has to go on, so it carries the brand
      // name rather than the lowercase row key.
      ariaLabel: "LinkedIn",
      available: !!contact.hasLinkedinUrl,
      skeletonWidth: "72px",
      // The whole line is one link target, so it owns its hover colour.
      isLink: true,
      value: contact.linkedinUrl ? { kind: "linkedin", url: contact.linkedinUrl } : null,
    },
  ];
}

/** Phone, then email, then LinkedIn — the order they render in, resting and revealed. */
export function contactChannels(contact: ContactChannelData): ChannelRow[] {
  return [...phoneRows(contact), ...emailRows(contact), ...linkedinRows(contact)];
}

/**
 * A channel is worth showing if it has something to say in the current state: a revealed
 * value, or (resting) an on-file flag driving the glyph. Empty channels drop out entirely.
 */
export function visibleChannels(contact: ContactChannelData, revealed: boolean): ChannelRow[] {
  return contactChannels(contact).filter((row) => (revealed ? !!row.value : row.available));
}
